/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import * as moment from 'moment';
import 'moment-timezone';
import * as _ from 'lodash';

import {CHUNK_SIZE} from '../../../components/api/resources/resources-constants';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {OgTranslateService} from '../filters/og-translate.service';
import {
  ADV_FIELDS,
  ADV_QUERY_FIELDS,
  ARTICLE_FIELDS,
  BURN_RATE_FIELDS,
  CDN_STATS_FIELDS,
  COLLECT_FIELDS,
  COLLECT_QUERY_FIELDS,
  EXTERNAL_AD_UNIT_QUERY_FIELDS,
  LINE_ITEM_QUERY_FIELDS,
  PUB_FIELDS,
  PUB_QUERY_FIELDS,
  SEGMENT_FIELDS,
  SEGMENT_QUERY_FIELDS,
  TARGETING_FIELDS
} from './chart-options';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

@Injectable({
  providedIn: 'root'
})
export class ChartOptionsUtil {
  private readonly options: any;
  private readonly lastChartDates: {[eventName: string]: any} = {};

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ogTranslate: OgTranslateService
  ) {
    const totalsFieldsMap = (data: any) => {
      return _.filter(_.map(data, (d: any) => {
        const another = _.assign({}, d);
        another.text = this.ogTranslate.transform('report.field.' + (another.tKey || another.id));
        another.abbr = another.abbr ? undefined : this.ogTranslate.transform('report.field.' + (another.tKey || another.id) + '.abbr');
        another.key = another.id;
        return another;
      }), (d: any) => d.id !== 'customFields');
    };

    const advTotalsFields = totalsFieldsMap(_.assign({}, ADV_QUERY_FIELDS));
    const lineItemTotalsFields = totalsFieldsMap(_.assign({}, LINE_ITEM_QUERY_FIELDS));
    const topArticlesFields = totalsFieldsMap(_.assign({}, ARTICLE_FIELDS));
    const creativeOnLineItemTotalsFields = advTotalsFields.slice();
    const advCrossTotalsFields = _.filter(advTotalsFields, (f: any) => {
      return f.key !== 'uniqueUsers' && f.key !== 'impressionsPerUniqueUser';
    });

    const pubTotalsFields = totalsFieldsMap(_.assign({}, PUB_QUERY_FIELDS));
    const extAdUnitTotalsFields = totalsFieldsMap(_.assign({}, EXTERNAL_AD_UNIT_QUERY_FIELDS));
    const collectTotalsFields = totalsFieldsMap(_.assign({}, COLLECT_QUERY_FIELDS));
    const segmentTotalsFields = totalsFieldsMap(_.assign({}, SEGMENT_QUERY_FIELDS));
    const targetingFields = totalsFieldsMap(_.assign({}, TARGETING_FIELDS));

    this.options = {
      ADVERTISER: {
        perfChartOptions: 'adnLineItemPerfChartOptions',
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        fields: ADV_FIELDS,
        queryFields: ADV_QUERY_FIELDS,
        targetingFields,
        totalsFields: advTotalsFields,
        chartTotalsFields: advTotalsFields,
        idKey: 'advertiserId'
      },
      CREATIVE: {
        perfChartOptions: 'adnLineItemPerfChartOptions',
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        adUnitRowsOptions: 'adnLineItemAdUnitRowsOptions',
        siteRowsOptions: 'adnLineItemSiteRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        targetingFields,
        chartTotalsFields: advTotalsFields,
        fields: ADV_FIELDS,
        queryFields: ADV_QUERY_FIELDS,
        totalsFields: advTotalsFields,
        adUnitFields: advCrossTotalsFields,
        siteFields: advCrossTotalsFields,
        idKey: 'creativeId'
      },
      ORDER: {
        perfChartOptions: 'adnLineItemPerfChartOptions',
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        lineItemRowsOptions: 'adnLineItemCreativeRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        fields: ADV_FIELDS,
        queryFields: ADV_QUERY_FIELDS,
        targetingFields,
        chartTotalsFields: advTotalsFields,
        totalsFields: advTotalsFields,
        lineItemFields: lineItemTotalsFields,
        siteFields: advCrossTotalsFields,
        idKey: 'orderId'
      },
      FOLIO: {
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        perfChartOptions: 'adnLineItemPerfChartOptions',
        creativeRowsOptions: 'adnFolioCreativeRowsOptions',
        creativeSetRowsOptions: 'adnFolioCreativeSetRowsOptions',
        adUnitRowsOptions: 'adnFolioAdUnitRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        fields: ADV_FIELDS,
        targetingFields,
        chartTotalsFields: advTotalsFields,
        queryFields: LINE_ITEM_QUERY_FIELDS,
        totalsFields: lineItemTotalsFields,
        creativeFields: creativeOnLineItemTotalsFields,
        adUnitFields: advCrossTotalsFields,
        idKey: 'folioId'
      },
      CREATIVE_SET: {
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        perfChartOptions: 'adnLineItemPerfChartOptions',
        creativeRowsOptions: 'adnCreativeSetCreativeRowsOptions',
        adUnitRowsOptions: 'adnCreativeSetAdUnitRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        fields: ADV_FIELDS,
        targetingFields,
        chartTotalsFields: advTotalsFields,
        queryFields: LINE_ITEM_QUERY_FIELDS,
        totalsFields: lineItemTotalsFields,
        creativeFields: creativeOnLineItemTotalsFields,
        adUnitFields: advCrossTotalsFields,
        idKey: 'creativeSetId'
      },
      LINE_ITEM: {
        queryColumnOptions: 'adnAdvertisingQueryOptions',
        perfChartOptions: 'adnLineItemPerfChartOptions',
        creativeRowsOptions: 'adnLineItemCreativeRowsOptions',
        siteRowsOptions: 'adnLineItemSiteRowsOptions',
        adUnitRowsOptions: 'adnLineItemAdUnitRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnLineItemChartTotalsOptions',
        fields: ADV_FIELDS,
        targetingFields,
        chartTotalsFields: advTotalsFields,
        queryFields: LINE_ITEM_QUERY_FIELDS,
        totalsFields: lineItemTotalsFields,
        topArticlesFields,
        creativeFields: creativeOnLineItemTotalsFields,
        adUnitFields: advCrossTotalsFields,
        siteFields: advCrossTotalsFields,
        idKey: 'lineItemId'
      },
      COLLECT: {
        perfChartOptions: 'adnCollectPerfChartOptions',
        queryColumnOptions: 'adnCollectQueryOptions',
        fields: COLLECT_FIELDS,
        queryFields: COLLECT_QUERY_FIELDS,
        totalsFields: collectTotalsFields,
        idKey: 'folderId'
      },
      FOLDER: {
        perfChartOptions: 'adnCollectPerfChartOptions',
        queryColumnOptions: 'adnCollectQueryOptions',
        fields: COLLECT_FIELDS,
        queryFields: COLLECT_QUERY_FIELDS,
        totalsFields: collectTotalsFields,
        idKey: 'folderId'
      },
      AD_UNIT: {
        type: 'PUBLISHING',
        queryColumnOptions: 'adnPublishingQueryOptions',
        perfChartOptions: 'adnAdUnitPerfChartOptions',
        lineItemRowsOptions: 'adnAdUnitLineItemRowsOptions',
        creativeRowsOptions: 'adnAdUnitCreativeRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnAdUnitChartTotalsOptions',
        targetingFields,
        fields: PUB_FIELDS,
        queryFields: PUB_QUERY_FIELDS,
        totalsFields: pubTotalsFields,
        lineItemFields: advCrossTotalsFields,
        creativeFields: advCrossTotalsFields,
        idKey: 'adUnitId'
      },
      SITE: {
        type: 'PUBLISHING',
        queryColumnOptions: 'adnPublishingQueryOptions',
        perfChartOptions: 'adnAdUnitPerfChartOptions',
        lineItemRowsOptions: 'adnSiteLineItemRowsOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnAdUnitChartTotalsOptions',
        fields: PUB_FIELDS,
        targetingFields,
        queryFields: PUB_QUERY_FIELDS,
        totalsFields: pubTotalsFields,
        lineItemFields: advCrossTotalsFields,
        idKey: 'siteId'
      },
      EXTERNAL_AD_UNIT: {
        type: 'PUBLISHING',
        perfChartOptions: 'adnExternalAdUnitPerfChartOptions',
        siteRowsOptions: 'adnExternalAdUnitSiteRowsOptions',
        adUnitRowsOptions: 'adnExternalAdUnitAdUnitRowsOptions',
        chartTotalsOptions: 'adnExternalAdUnitChartTotalsOptions',
        fields: EXTERNAL_AD_UNIT_QUERY_FIELDS,
        totalsFields: extAdUnitTotalsFields,
        idKey: 'externalAdUnitId',
        adUnitFields: extAdUnitTotalsFields,
        siteFields: extAdUnitTotalsFields
      },
      EXTERNAL_DEMAND_SOURCE: {
        perfChartOptions: 'adnExternalDemandSourcePerfChartOptions',
        fields: EXTERNAL_AD_UNIT_QUERY_FIELDS,
        totalsFields: extAdUnitTotalsFields,
        idKey: 'externalDemandSourceId',
        adUnitFields: extAdUnitTotalsFields,
        siteFields: extAdUnitTotalsFields
      },
      EARNINGS_ACCOUNT: {
        type: 'PUBLISHING',
        queryColumnOptions: 'adnPublishingQueryOptions',
        perfChartOptions: 'adnAdUnitPerfChartOptions',
        targetingColumnOptions: 'adnTargetingQueryRowsOptions',
        chartTotalsOptions: 'adnAdUnitChartTotalsOptions',
        targetingFields,
        lineItemRowsOptions: 'adnEarningsAccountLineItemRowsOptions',
        lineItemFields: advCrossTotalsFields,
        fields: PUB_FIELDS,
        queryFields: PUB_QUERY_FIELDS,
        totalsFields: pubTotalsFields,
        idKey: 'earningsAccountId'
      },
      SEGMENT: {
        perfChartOptions: 'adnSegmentPerfChartOptions',
        fields: SEGMENT_FIELDS,
        totalsFields: segmentTotalsFields,
        idKey: 'segmentId'
      },
      USER_SEGMENT: {
        fields: ADV_FIELDS,
        lineItemRowsOptions: 'adnUserSegmentLineItemRowsOption',
        queryColumnOptions: 'adnUserSegmentQueryOptions',
        totalsFields: advCrossTotalsFields,
        lineItemFields: advCrossTotalsFields,
        idKey: 'segmentId'
      },
      BURN_RATE: {
        fields: BURN_RATE_FIELDS
      },
      CDN_STATS: {
        perfChartOptions: 'adnCdnStatsOptions',
        fields: CDN_STATS_FIELDS
      }
    };
  }

  getCustomFieldsAverage(): string[] {
    return _.map(_.filter(LocalNetworkInfo.obtain().getInfo('customEvents'), (ce: any) => {
      return ce.type === 'SUM';
    }), 'name');
  }

  getSplitFees(dataPoint: any): any[] {
    const splitFees: any[] = [];
    _.forEach(_.get(dataPoint, ['feeBreakdown']), (v: any, k: string) => {
      splitFees.push({name: k, val: v});
    });
    return splitFees;
  }

  getLineItemCustomFields(dataPoint: any): string[] {
    const customFields: any[] = [];
    const customEventsInOrder = LocalNetworkInfo.obtain().getInfo('customEvents');
    _.forEach(dataPoint, (v: any, k: string) => {
      if (_.get(v, 'id')) {
        const match = _.find(customEventsInOrder, (ce: any) => ce.id === v.id);
        const priority = _.get(match, ['priority'], 0);
        customFields.push({name: k, priority: priority === 0 ? Number.MAX_SAFE_INTEGER : priority});
      }
    });

    const sortedCustom = _.sortBy(customFields, ['priority']);
    return _.map(sortedCustom, 'name');
  }

  getNetworkTimezone(): string {
    return this.LocalNetworkProfile.get('timeZone') || 'utc';
  }

  getChunkSize(startDate: any, endDate: any, absoluteChunkMin?: string, isTargeting?: boolean): any {
    const chunk = this.getTheChunkSize(startDate, endDate, isTargeting);
    const minChunk = absoluteChunkMin ? CHUNK_SIZE[absoluteChunkMin] : null;
    if (minChunk) {
      return minChunk.closestMinutes > chunk.closestMinutes ? minChunk : chunk;
    }
    return chunk;
  }

  getOptions(objType: string): any {
    const theOptions = this.options[objType];
    if (theOptions && theOptions.fields) {
      if (this.LocalNetworkProfile.isPureMarketplacePlatform()) {
        this.skipFields(theOptions, 'notMarketplaceOnly');
        if (!this.LocalNetworkProfile.hasCapability('RETAIL_ADS')) {
          this.skipFields(theOptions, 'directOrRetailOnly');
        }
      }
      if (!this.LocalNetworkProfile.isPureMarketplacePlatform()) {
        this.skipFields(theOptions, 'marketplaceOnly');
      }
      if (!this.LocalNetworkProfile.isPureMarketplacePlatform() && !this.LocalNetworkProfile.isSuiPlatform()) {
        this.skipFields(theOptions, 'marketplaceOrSelfServiceOnly');
      }
      if (!this.LocalNetworkProfile.isGdprEnabled()) {
        this.skipFields(theOptions, 'gdprEnabledOnly');
      }
    }
    return theOptions;
  }

  getDateOptions(chartType?: string): any {
    const dateOptions: any = {
      last6Hours: this.lastNHours(6),
      last12Hours: this.lastNHours(12),
      last24Hours: this.lastNHours(24),
      today: {
        id: 'today',
        startDate: () => this.getNowInTz().startOf('day'),
        endDate: this.lastBucket
      },
      yesterday: {
        id: 'yesterday',
        startDate: () => this.getNowInTz().startOf('day').subtract(1, 'd'),
        endDate: () => this.getNowInTz().subtract(1, 'd').endOf('day')
      },
      last7Days: {
        id: 'last7Days',
        startDate: () => this.getNowInTz().startOf('day').subtract(7, 'd'),
        endDate: this.lastBucket
      },
      thisWeek: {
        id: 'thisWeek',
        startDate: () => {
          const defaults = this.LocalNetworkProfile.getDefaults();
          const startDay = defaults.startDayCalendar;
          return _.isFinite(startDay) ? this.getNowInTz().isoWeekday(startDay).startOf('isoWeek') : this.getNowInTz().startOf('week');
        },
        endDate: this.lastBucket
      },
      lastWeek: {
        id: 'lastWeek',
        startDate: () => {
          const defaults = this.LocalNetworkProfile.getDefaults();
          const startDay = defaults.startDayCalendar;
          const beginningPoint = this.getNowInTz().subtract(1, 'week');
          return _.isFinite(startDay) ? beginningPoint.isoWeekday(startDay).startOf('isoWeek') : beginningPoint.startOf('week');
        },
        endDate: () => {
          const defaults = this.LocalNetworkProfile.getDefaults();
          const startDay = defaults.startDayCalendar;
          const beginningPoint = this.getNowInTz().subtract(1, 'week');
          return _.isFinite(startDay) ? beginningPoint.isoWeekday(startDay).endOf('isoWeek') : beginningPoint.endOf('week');
        }
      },
      last30Days: {
        id: 'last30Days',
        startDate: () => this.getNowInTz().startOf('day').subtract(30, 'd'),
        endDate: this.lastBucket
      },
      thisMonth: {
        id: 'thisMonth',
        startDate: () => this.getNowInTz().startOf('month'),
        endDate: this.lastBucket
      },
      lastMonth: {
        id: 'lastMonth',
        startDate: () => this.getNowInTz().subtract(1, 'month').startOf('month'),
        endDate: () => this.getNowInTz().subtract(1, 'month').endOf('month').endOf('day')
      },
      last6Months: {
        id: 'last6Months',
        startDate: () => this.getNowInTz().subtract(6, 'month').startOf('day'),
        endDate: this.lastBucket
      },
      last12Months: {
        id: 'last12Months',
        startDate: () => this.getNowInTz().subtract(12, 'month').startOf('month'),
        endDate: this.lastBucket
      },
      thisYear: {
        id: 'thisYear',
        startDate: () => this.getNowInTz().startOf('year'),
        endDate: this.lastBucket
      },
      lastYear: {
        id: 'lastYear',
        startDate: () => this.getNowInTz().subtract(1, 'year').startOf('year'),
        endDate: () => this.getNowInTz().subtract(1, 'year').endOf('year').endOf('day')
      }
    };
    if (chartType === 'ORDER' || chartType === 'LINE_ITEM' || chartType === 'CREATIVE' || chartType === 'CREATIVE_SET' || chartType === 'FOLIO') {
      dateOptions.useDates = {
        id: 'useDates',
        startDate: () => undefined,
        endDate: () => undefined,
        undated: true
      };
    }
    return dateOptions;
  }

  private getNowInTz(): moment.Moment {
    return moment().tz(this.getNetworkTimezone());
  }

  private lastBucket = (): moment.Moment => {
    const now = this.getNowInTz();
    const duration = Number(CHUNK_SIZE.HALF_HOURLY.duration.valueOf());
    return moment(Math.ceil(now.valueOf() / duration) * duration);
  };

  private lastNHours(n: number): any {
    let id = 'last' + n + 'Hour';
    if (n !== 1) {
      id += 's';
    }
    return {
      id,
      startDate: () => this.getNowInTz().subtract(n, 'h').startOf('h'),
      endDate: this.lastBucket
    };
  }

  private getTheChunkSize(startDate: any, endDate: any, isTargeting?: boolean): any {
    const hourlyDays = isTargeting ? 14 : 44;
    if (startDate && endDate) {
      const dateDiff = Math.abs(moment(startDate).diff(moment(endDate), 'days'));
      const daysFromNow = Math.max(Math.abs(moment(startDate).diff(moment(), 'days')), Math.abs(moment(endDate).diff(moment(), 'days')));
      if (dateDiff < 2 && daysFromNow < hourlyDays) {
        return CHUNK_SIZE.HALF_HOURLY;
      }
      if (dateDiff < 5 && daysFromNow < hourlyDays) {
        return CHUNK_SIZE.HOURLY;
      }
      if (dateDiff < 65) {
        return CHUNK_SIZE.DAILY;
      }
      if (dateDiff < 350) {
        return CHUNK_SIZE.WEEKLY;
      }
      return CHUNK_SIZE.MONTHLY;
    }
    return CHUNK_SIZE.MONTHLY;
  }

  setLastChartDates(eventName: string, dates: any): void {
    this.lastChartDates[eventName] = dates;
  }

  getLastChartDates(eventName: string): any {
    return this.lastChartDates[eventName];
  }

  private skipFields(theOptions: any, checkField: string): any {
    const skippableFields = _.filter(theOptions.fields, (f: any) => f[checkField]);
    _.forEach(Object.keys(theOptions), (param: string) => {
      if (_.endsWith(param, 'ields')) {
        _.forEach(skippableFields, (f: any) => {
          if (theOptions[param][f.id]) {
            delete theOptions[param][f.id];
          } else {
            theOptions[param] = _.filter(theOptions[param], (theOptionF: any) => {
              return theOptionF.id !== f.id;
            });
          }
        });
      }
    });
    return theOptions;
  }
}
